import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { MainLayout, PublicLayout, ProfileLayout } from './components/layout';
import DesignSystem from './pages/DesignSystem';
import { Profile } from './pages/Profile';
import { Quotes } from './pages/Quotes';
import { Landing } from './pages/Landing';
import { SHOW_DESIGN_PAGE } from './config/debug';
import { AuthProvider, OIDCCallback, ProtectedRoute } from './pages/Auth';
import { useAuthStore } from './stores/authStore';
import { QuoteDetailComponent, QuoteFormComponent } from './pages/Quotes/components';
import { PolicyList, PolicyForm } from './pages/Policies';
import { PolicyAdmin } from './pages/PolicyAdmin';
import { Reporting } from './pages/Reporting';

const AppContent: React.FC = () => {
  const { checkAuth, loading, isAuthenticated } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent"></div>
          <p className="text-[var(--color-text-primary)]">Loading...</p>
        </div>
      </div>
    );
  }

  const getActiveItem = () => {
    const path = location.pathname;
    if (path === '/quotes' || path.startsWith('/quotes/')) return 'quotes';
    if (path.startsWith('/policy-admin')) return 'policy-admin';
    if (path.startsWith('/reporting')) return 'reporting';
    if (path === '/policies' || path.startsWith('/policies/')) return 'policies';
    if (path === '/design') return 'design-system';
    return 'quotes';
  };

  return (
    <Routes>
      <Route path="/sso/callback" element={<OIDCCallback />} />
      {isAuthenticated ? (
        <>
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileLayout>
                  <Profile />
                </ProfileLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainLayout activeItem={getActiveItem()}>
                  <Routes>
                    <Route index element={<Quotes />} />
                    <Route path="quotes" element={<Quotes />} />
                    <Route path="quotes/new" element={<QuoteFormComponent />} />
                    <Route path="quotes/:id" element={<QuoteDetailComponent />} />
                    <Route path="quotes/:id/edit" element={<QuoteFormComponent />} />
                    <Route path="policies" element={<PolicyList />} />
                    <Route path="policies/new" element={<PolicyForm />} />
                    <Route path="policy-admin/*" element={<PolicyAdmin />} />
                    <Route path="reporting/*" element={<Reporting />} />
                    {SHOW_DESIGN_PAGE && <Route path="design" element={<DesignSystem />} />}
                  </Routes>
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </>
      ) : (
        <Route
          path="/*"
          element={
            <PublicLayout>
              <Routes>
                <Route index element={<Landing />} />
                <Route path="*" element={<Landing />} />
              </Routes>
            </PublicLayout>
          }
        />
      )}
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
